import React, { useState } from "react";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle2,
  MessageSquare,
  Building2,
  Sparkles,
  ArrowRight,
  HelpCircle,
  ShieldCheck,
  Headphones,
} from "lucide-react";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API request
    setTimeout(() => {
      setLoading(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
        });
      }, 4000);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Headquarters",
      lines: ["No 33 Old Mahabaliburam Road, Seevaram , Perungudi, Chennai 600096."],
      actionText: "Get Directions",
      actionUrl: "https://maps.google.com?q=OMR+Road+Chennai",
    },
    {
      icon: Phone,
      title: "Direct Line",
      lines: ["+91 95666 63558", "Toll Free: 1800-123-4567"],
      actionText: "Call Now",
      actionUrl: "tel:+919566663558",
    },
    {
      icon: Mail,
      title: "Email Support",
      lines: ["info@inim.ai", "support@inim.ai"],
      actionText: "Send Mail",
      actionUrl: "mailto:info@inim.ai",
    },
    {
      icon: Clock,
      title: "Business Hours",
      lines: ["Monday - Saturday: 9:00 AM - 6:00 PM", "Sunday: Closed"],
      actionText: null,
      actionUrl: null,
    },
  ];

  const faqs = [
    {
      q: "How quickly can I expect a response?",
      a: "Our team typically responds to all inquiries within 2 to 4 business hours.",
    },
    {
      q: "Do you offer custom AI & enterprise software consultations?",
      a: "Yes! We schedule free 30-minute discovery calls with our solution architects.",
    },
  ];

  return (
    <div className="bg-[#f8fafc] text-slate-800 font-sans min-h-screen">
      {/* ================= HERO HEADER ================= */}
      <section className="relative bg-slate-950 text-white pt-24 pb-28 px-6 overflow-hidden">
        {/* Ambient Gradient Glows */}
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#f25c05]/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-amber-600/10 rounded-full blur-[140px] pointer-events-none" />

        {/* Grid Overlay Texture */}
        <div 
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(#ffffff 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        />

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 border border-slate-800 text-[#f25c05] text-xs font-bold uppercase tracking-widest mb-6 backdrop-blur-md shadow-sm">
            <Sparkles size={14} className="animate-pulse" />
            Connect With INIM.AI Solutions
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-tight">
            Ready to Build something <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-[#f25c05] via-orange-400 to-amber-500 bg-clip-text text-transparent">
              Extraordinary?
            </span>
          </h1>

          <p className="mt-6 text-slate-400 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed font-normal">
            Have a project in mind, need technical support, or want to explore our AI solutions? 
            Our expert engineers and consultants are here to help.
          </p>
        </div>
      </section>

      {/* ================= MAIN CONTENT ================= */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 -mt-16 relative z-20 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT COLUMN: Modern Form Section (7 cols) */}
          <div className="lg:col-span-7 bg-white/90 backdrop-blur-xl p-6 sm:p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100">
            <div className="flex items-center justify-between pb-6 mb-8 border-b border-slate-100">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
                  <MessageSquare className="text-[#f25c05]" size={26} />
                  Send Us a Message
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  Fill out the form and our technical team will reach out shortly.
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 font-medium bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200/60">
                <ShieldCheck size={14} className="text-emerald-500" />
                100% Secure & Confidential
              </div>
            </div>

            {isSubmitted ? (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/80 text-emerald-900 p-10 rounded-2xl text-center space-y-4 my-8">
                <div className="w-16 h-16 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30">
                  <CheckCircle2 size={36} />
                </div>
                <h3 className="text-2xl font-bold">Message Received!</h3>
                <p className="text-sm text-emerald-700 max-w-md mx-auto leading-relaxed">
                  Thank you for contacting INIM.AI. A technical specialist has been assigned to your query and will reply within 2 business hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Name Input */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      Your Full Name <span className="text-[#f25c05]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. Alex Morgan"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50/50 border border-slate-200/80 focus:bg-white focus:border-[#f25c05] focus:ring-4 focus:ring-[#f25c05]/10 outline-none transition text-sm text-slate-800 font-medium placeholder:text-slate-400"
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      Email Address <span className="text-[#f25c05]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="alex@company.com"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50/50 border border-slate-200/80 focus:bg-white focus:border-[#f25c05] focus:ring-4 focus:ring-[#f25c05]/10 outline-none transition text-sm text-slate-800 font-medium placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Phone Input */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      Phone Number <span className="text-[#f25c05]">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50/50 border border-slate-200/80 focus:bg-white focus:border-[#f25c05] focus:ring-4 focus:ring-[#f25c05]/10 outline-none transition text-sm text-slate-800 font-medium placeholder:text-slate-400"
                    />
                  </div>

                  {/* Subject Dropdown */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      What can we help with?
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50/50 border border-slate-200/80 focus:bg-white focus:border-[#f25c05] focus:ring-4 focus:ring-[#f25c05]/10 outline-none transition text-sm text-slate-800 font-medium cursor-pointer"
                    >
                      <option value="">Select Option</option>
                      <option value="AI Solutions">AI & Machine Learning</option>
                      <option value="Full-Stack Web">Full-Stack Development</option>
                      <option value="Cloud Services">Cloud & Migration</option>
                      <option value="IT Consulting">IT Consulting & Strategy</option>
                      <option value="General Inquiry">Other Inquiry</option>
                    </select>
                  </div>
                </div>

                {/* Message Textarea */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                    Project Details or Message <span className="text-[#f25c05]">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us briefly about your goals, timeline, or scope..."
                    className="w-full px-4 py-3.5 rounded-xl bg-slate-50/50 border border-slate-200/80 focus:bg-white focus:border-[#f25c05] focus:ring-4 focus:ring-[#f25c05]/10 outline-none transition text-sm text-slate-800 font-medium placeholder:text-slate-400 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#f25c05] hover:bg-[#d84e00] active:scale-[0.99] text-white font-bold py-4 px-8 rounded-xl transition duration-200 flex items-center justify-center gap-2.5 shadow-lg shadow-[#f25c05]/25 hover:shadow-xl hover:shadow-[#f25c05]/35 disabled:opacity-70 cursor-pointer text-base"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* RIGHT COLUMN: Info Cards & Interactive Map (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Quick Contact Info Cards */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Building2 className="text-[#f25c05]" size={22} />
                Contact Details
              </h3>

              <div className="space-y-4">
                {contactInfo.map((card, idx) => {
                  const IconComp = card.icon;
                  return (
                    <div
                      key={idx}
                      className="p-4 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-orange-200/80 hover:shadow-md transition-all duration-300 group flex items-start gap-4"
                    >
                      <div className="p-3 bg-white group-hover:bg-[#f25c05] text-[#f25c05] group-hover:text-white rounded-xl shadow-sm border border-slate-100 group-hover:border-transparent transition-all duration-300 shrink-0">
                        <IconComp size={20} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-slate-900 text-sm">{card.title}</h4>
                          {card.actionUrl && (
                            <a
                              href={card.actionUrl}
                              target={card.actionUrl.startsWith("http") ? "_blank" : "_self"}
                              rel="noreferrer"
                              className="text-xs font-bold text-[#f25c05] hover:text-[#d84e00] flex items-center gap-0.5 opacity-90 group-hover:opacity-100"
                            >
                              {card.actionText}
                              <ArrowRight size={12} />
                            </a>
                          )}
                        </div>
                        <div className="mt-1 text-xs sm:text-sm text-slate-600 space-y-0.5 leading-snug">
                          {card.lines.map((line, i) => (
                            <p key={i} className="truncate">{line}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Live Interactive Location Map */}
            <div className="bg-white p-3 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden bg-slate-100">
                <iframe
                  title="INIM.AI Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.751680197775!2d80.2281!3d12.9234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525cf155555555%3A0x123456789abcdef!2sOMR%20Road%2C%20Chennai!5e0!3m2!1sen!2sin!4v1680000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale contrast-125 opacity-90 hover:grayscale-0 hover:opacity-100 transition duration-500"
                />
                
                {/* Overlay Badge */}
                <div className="absolute bottom-3 left-3 bg-slate-900/90 text-white backdrop-blur-md px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 border border-slate-700">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                  Chennai Tech Campus
                </div>
              </div>
            </div>

            {/* Quick FAQ Card */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-950 text-white p-6 sm:p-8 rounded-3xl shadow-xl border border-slate-800">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-[#f25c05]">
                <HelpCircle size={20} />
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="border-b border-slate-800/80 pb-3 last:border-0 last:pb-0">
                    <h4 className="text-xs font-semibold text-slate-200">{faq.q}</h4>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>
      </main>
    </div>
  );
}